import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDropdownReturn<T> {
	isOpen: boolean;
	filterText: string;
	filteredOptions: T[];
	containerRef: React.RefObject<HTMLDivElement>;
	inputRef: React.RefObject<HTMLInputElement>;
	listRef?: React.RefObject<HTMLDivElement>;
	selectedOption: T | undefined;
	handleToggle: () => void;
	handleSelect: (option: T) => void;
	handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleInputKeyDown: (e: React.KeyboardEvent) => void;
}

export function useDropdown<T extends { value: string; label: string }>(
	options: T[],
	value: string,
	onChange: (value: string) => void,
): UseDropdownReturn<T> {
	const [isOpen, setIsOpen] = useState(false);
	const [filterText, setFilterText] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find((opt) => opt.value === value);

	const filteredOptions = options.filter((opt) => {
		if (!filterText.trim()) return true;
		const lowerFilter = filterText.toLowerCase();
		return (
			opt.value.toLowerCase().includes(lowerFilter) ||
			opt.label.toLowerCase().includes(lowerFilter)
		);
	});

	const handleToggle = useCallback(() => {
		setIsOpen((prev) => !prev);
		if (!isOpen) {
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [isOpen]);

	const handleSelect = useCallback(
		(option: T) => {
			onChange(option.value);
			setIsOpen(false);
			setFilterText('');
		},
		[onChange],
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setFilterText(e.target.value);
		},
		[],
	);

	const handleInputKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false);
				setFilterText('');
			} else if (e.key === 'Enter' && filteredOptions.length > 0) {
				handleSelect(filteredOptions[0]);
			}
		},
		[filteredOptions, handleSelect],
	);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				setFilterText('');
			}
		}

		let timeoutId: NodeJS.Timeout;
		if (isOpen) {
			timeoutId = setTimeout(() => {
				document.addEventListener('click', handleClickOutside);
			}, 300);
		}

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isOpen]);

	return {
		isOpen,
		filterText,
		filteredOptions,
		containerRef,
		inputRef,
		listRef,
		selectedOption,
		handleToggle,
		handleSelect,
		handleInputChange,
		handleInputKeyDown,
	};
}
